/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Modal, Grid, Row, Typography, Input, Select } from 'antd';

// Misc imports
import { useCreateKnowledgeBaseArticleMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import RichTextEditor from '../Forms/Fields/RichTextEditor/RichTextEditor';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../constants/markdown';
import { ARTICLE_TITLE_MAX_LENGTH, KNOWLEDGE_BASE_ARTICLE_STATUSES } from '../../constants/knowledgeBases';

// Constant declarations
const { Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const CreateHRWizKnowledgeBaseArticleModal = ({
  isModalOpen = false,
  closeModal = () => {},
  onCreated = () => {},
  topics = {},
  activeKBs = []
}) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const [createArticle, { isLoading: isCreatingArticle }] = useCreateKnowledgeBaseArticleMutation();
  const { data: bots } = useFetchBotsQuery({ orgId });
  const primaryBotLanguage = bots?.items.find(bot => bot.id === tenantId)?.settings?.languages?.primary || 'en';

  const handleOk = () => {
    form.submit();
  };

  const onFormSubmit = async (values) => {
    const knowledgeBaseId = values?.source;
    delete values?.source;
    const data = {
      content: [{
        ...values,
        tags: [topics[values.tags]],
        languageCode: primaryBotLanguage
      }],
      status: KNOWLEDGE_BASE_ARTICLE_STATUSES.PUBLISHED
    };

    try {
      const isArticleCreated = await createArticle({ orgId, knowledgeBaseId, data }).unwrap();
      if (isArticleCreated != null) {
        await onCreated();
        form.resetFields();
        closeModal();
      }
    } catch (e) {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    form.resetFields();
    closeModal();
  };

  return (
    <Modal
      title={
        <Paragraph className="d-flex mb-0">
          {t('Create a new article')}
        </Paragraph>
      }
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={handleOk}
      okText={t('Create')}
      confirmLoading={isCreatingArticle}
      centered
      className="flex-auto overflow-y-auto px-6 py-5 w-50"
      destroyOnClose
      maskClosable={false}
    >
      <Row wrap={!!isMobileOrTabletSize} className="h-100">
        <Col className="px-5" flex="auto">
          <Form
            className="my-4"
            form={form}
            labelCol={{ xs: 24, sm: 24, md: 6, xl: 4 }}
            wrapperCol={{ xs: 24, sm: 24, md: 12, xl: 10 }}
            colon={false}
            shouldUpdate
            onFinish={onFormSubmit}
          >

            <Form.Item
              name="title"
              label={<Text type="secondary" style={{ minWidth: 100 }}>{t('Title')}</Text>}
              labelAlign="left"
              validateTrigger={['onChange', 'onBlur']}
              rules={[{
                required: true,
                type: 'string',
                whitespace: true,
                message: t('Please enter a title for this article')
              }, {
                validator: async (rule, value) => {
                  try {
                    if (value && value.length > ARTICLE_TITLE_MAX_LENGTH) {
                      throw new Error(rule.message);
                    }
                  } catch {
                    throw new Error(rule.message);
                  }
                },
                message: t('Exceeded character limit')
              }]}
            >
              <Input
                placeholder={t('Enter article title')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="tags"
              label={<Text type="secondary">{t('Topic')}</Text>}
              labelAlign="left"
              rules={[{
                required: true,
                type: 'string',
                whitespace: true,
                message: t('Please select a topic for this article')
              }]}
            >
              <Select
                size="large"
                tokenSeparators={[',']}
                placeholder={t('Select a topic for the article')}
                options={
                  Object.keys(topics)
                    .map(tmp => ({ label: topics[tmp], value: tmp }))
                    .sort((a, b) => a.value === 'other' ? 1 : b.value === 'other' ? -1 : a.label >= b.label ? 1 : -1)
                }
              />
            </Form.Item>

            <Form.Item
              name="source"
              label={<Text type="secondary">{t('Source')}</Text>}
              labelAlign="left"
              rules={[{
                required: true,
                type: 'string',
                whitespace: true,
                message: t('Please select a source for this article')
              }]}
            >
              <Select
                size="large"
                tokenSeparators={[',']}
                placeholder={t('Select a source for the article')}
                options={
                  activeKBs.map(kb => ({ label: kb?.label, value: kb?.id }))
                }
              />
            </Form.Item>

            <Form.Item
              name={'body'}
              label={<Text type="secondary">{t('Body')}</Text>}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              rules={[{
                required: true,
                type: 'string',
                whitespace: true,
                message: t('Please enter a body for this article')
              }]}
            >
              <RichTextEditor
                toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.HEADING, RICH_TEXT_TOOLBAR_ACTIONS.BOLD, RICH_TEXT_TOOLBAR_ACTIONS.ITALIC, RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST, RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST, RICH_TEXT_TOOLBAR_ACTIONS.LINK, RICH_TEXT_TOOLBAR_ACTIONS.EMOJI]}
                placeholder={t('Enter article body')}
                style={{ height: 200, maxHeight: 600 }}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

CreateHRWizKnowledgeBaseArticleModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  topics: PropTypes.object,
  activeKBs: PropTypes.array,
  onCreated: PropTypes.func
};

export default CreateHRWizKnowledgeBaseArticleModal;
